import themaFallbackBisac from "./mappings/thema_fallback_bisac.js";
import themaToBisacMapping from "./mappings/thema_to_bisac_mapping.json";

export default function mapThemaToBisac(themaCodes = []) {
	const { subjects, qualifiers, all } = partitionCodes(themaCodes); // { subjects:Set, qualifiers:Set, all:Set }

	if (!all.size) {
		const fallback = findFallbackMatch(themaCodes);
		if (fallback) {
			return { matches: [fallback], unmatched: [] };
		}
		return { matches: [], unmatched: themaCodes };
	}

	const bestMatches = findBestMatches({ subjects, qualifiers, all });

	if (bestMatches.size === 0) {
		const fallback = findFallbackMatch(themaCodes);
		if (fallback) {
			return { matches: [fallback], unmatched: [] };
		}
		return { matches: [], unmatched: themaCodes };
	}

	const candidates = Array.from(bestMatches.entries()).map(([bisac, meta]) => ({ bisac, meta }));

	const survivors = removeDominatedCandidates(candidates);
	const winners = pickWinnerAmongSurvivors(survivors);

	const matches = [];
	const usedThemaCodes = new Set();

	for (const { bisac, meta } of winners) {
		matches.push({ bisac, themaDescriptions: meta.themaDescriptions });

		// mark only what actually matched: all required subjects and any qualifiers we truly had
		for (const code of meta.subjectSet) usedThemaCodes.add(code);
		for (const code of meta.matchedQualifierSet) usedThemaCodes.add(code);
	}

	const unmatched = findUnmatches({ themaCodes, usedThemaCodes });
	return { matches, unmatched };
}

function partitionCodes(themaCodes) {
	const subjects = new Set();
	const qualifiers = new Set();
	const all = new Set();

	for (const code of themaCodes) {
		if (isTopLevelSubject(code)) {
			// Ignore top-level headings
			continue;
		}

		all.add(code);

		if (isSubject(code)) {
			subjects.add(code);
		} else if (isQualifier(code)) {
			qualifiers.add(code);
		} else {
			// Safe default
			subjects.add(code);
		}
	}

	return { subjects, qualifiers, all };
}

/*
  Find best match per BISAC, allowing missing qualifiers.
  Rule: all required subjects must be present; qualifiers optional.
  Score: subjectsCount*100 + matchedQualifiersCount (subjects dominate).
*/
function findBestMatches({ subjects, qualifiers, all }) {
	const bisacBest = new Map();

	for (const themaCode of all) {
		const bisacCombosForSubject = themaToBisacMapping[themaCode];
		if (!bisacCombosForSubject) {
			continue;
		}

		for (const [bisacCode, { themaCodes, themaDescriptions, seq }] of Object.entries(bisacCombosForSubject)) {
			const bisacSubjects = themaCodes.filter(isSubject);
			const bisacQualifiers = themaCodes.filter(isQualifier);

			if (!bisacSubjects.every((c) => subjects.has(c))) {
				continue;
			}

			const matchedQualifierSet = new Set(bisacQualifiers.filter((c) => qualifiers.has(c)));
			const matchedQualifierCount = matchedQualifierSet.size;
			const qualifierCount = new Set([...bisacQualifiers]).size;
			const coverage = qualifierCount === 0 ? 0 : matchedQualifierCount / qualifierCount;

			// primary score: subjects dominate
			const score = new Set([...bisacSubjects]).size * 100 + matchedQualifierCount;

			const prev = bisacBest.get(bisacCode);
			const better =
				!prev ||
				score > prev.score ||
				(score === prev.score &&
					(coverage > prev.coverage ||
						(coverage === prev.coverage &&
							(qualifierCount < prev.qualifierCount ||
								(qualifierCount === prev.qualifierCount && seq < prev.seq)))));

			if (better) {
				bisacBest.set(bisacCode, {
					subjectSet: new Set([...bisacSubjects]),
					matchedQualifierSet,
					qualifierCount,
					coverage,
					score,
					seq,
					themaDescriptions,
				});
			}
		}
	}
	return bisacBest;
}

/*
  Dominance: compare only subject sets.
  B dominates A if B's required subjects ⊃ A's required subjects.
*/
function removeDominatedCandidates(candidates) {
	const dominated = new Array(candidates.length).fill(false);

	for (let a = 0; a < candidates.length; a++) {
		const A = candidates[a].meta;
		for (let b = 0; b < candidates.length; b++) {
			if (a === b) continue;
			const B = candidates[b].meta;

			const subjectsCovered = isSubset(A.subjectSet, B.subjectSet);
			const strictlyMore = A.subjectSet.size < B.subjectSet.size;

			if (subjectsCovered && strictlyMore) {
				dominated[a] = true;
				break;
			}
		}
	}
	return candidates.filter((_, i) => !dominated[i]);
}

function pickWinnerAmongSurvivors(survivors) {
	const winners = [];
	for (const candidate of survivors) {
		const i = winners.findIndex((w) => setsAreEqual(w.meta.subjectSet, candidate.meta.subjectSet));
		if (i === -1) {
			winners.push(candidate);
			continue;
		}

		const cur = winners[i];
		const a = cur.meta;
		const b = candidate.meta;

		const bBetter =
			// 1) higher qualifier coverage (prefer mappings whose required qualifiers we actually provide)
			b.coverage > a.coverage ||
			(b.coverage === a.coverage &&
				// 2) fewer required qualifiers (prefer the simpler mapping when we lack qualifiers)
				b.qualifierCount < a.qualifierCount) ||
			(b.coverage === a.coverage &&
				b.qualifierCount === a.qualifierCount &&
				// 3) lower sequence wins
				b.seq < a.seq);

		if (bBetter) {
			winners[i] = candidate;
		}
	}
	return winners;
}

function findUnmatches({ themaCodes, usedThemaCodes }) {
	return themaCodes.filter((code) => {
		if (isTopLevelSubject(code)) {
			// Ignore top level headings, they will never match directly
			return false;
		}
		return !usedThemaCodes.has(code);
	});
}

function isTopLevelSubject(c) {
	return /^[A-Z]$/.test(c); // single-letter headings
}

function isSubject(code) {
	return /^[A-Z]/.test(code);
}

function isQualifier(code) {
	return /^[0-9]/.test(code);
}

function isSubset(aSet, bSet) {
	for (const x of aSet) if (!bSet.has(x)) return false;
	return true;
}

function setsAreEqual(a, b) {
	return a.size === b.size && [...a].every((x) => b.has(x));
}

function findFallbackMatch(themaCodes) {
	for (const code of themaCodes) {
		const topLevel = code[0];
		if (themaFallbackBisac[topLevel]) {
			return { bisac: themaFallbackBisac[topLevel], themaDescriptions: [] };
		}
	}
	return null;
}
